import React, { useState } from 'react';
import { CheckCircle, Circle, Package, ClipboardList, AlertCircle } from 'lucide-react';

interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
}

interface PreSessionChecklistProps {
  clientName: string;
  onComplete: (checkedItems: Record<string, boolean>) => void;
  onSkip?: () => void;
}

export const PreSessionChecklist: React.FC<PreSessionChecklistProps> = ({
  clientName,
  onComplete,
  onSkip,
}) => {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>({});

  const checklistItems: ChecklistItem[] = [
    { id: 'materials', label: 'Session materials and supplies ready', required: true },
    { id: 'dataSheets', label: 'Data collection sheets prepared', required: true },
    { id: 'reinforcers', label: 'Client reinforcers available', required: true },
    { id: 'programs', label: 'Reviewed today\'s programs and goals', required: true },
    { id: 'safety', label: 'Safety equipment checked (if applicable)', required: false },
    { id: 'parentContact', label: 'Parent/guardian contact confirmed', required: true },
    { id: 'workspace', label: 'Session area prepared and safe', required: true },
  ];

  const handleToggleItem = (itemId: string) => {
    setCheckedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const requiredItems = checklistItems.filter((item) => item.required);
  const requiredChecked = requiredItems.filter((item) => checkedItems[item.id]).length;
  const allRequiredChecked = requiredChecked === requiredItems.length;

  const handleContinue = () => {
    if (allRequiredChecked) {
      onComplete(checkedItems);
    }
  };

  // Inline styles
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    headerBox: {
      backgroundColor: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      padding: '16px',
    },
    headerContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    headerIcon: {
      width: '24px',
      height: '24px',
      color: '#4F6367',
      marginTop: '2px',
      flexShrink: 0,
    },
    headerTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: '#1e3a8a',
      margin: 0,
    },
    headerSubtitle: {
      fontSize: '14px',
      color: '#3d4d50',
      marginTop: '4px',
      margin: 0,
    },
    headerHint: {
      fontSize: '12px',
      color: '#4F6367',
      marginTop: '8px',
    },
    progressContainer: {
      marginBottom: '8px',
    },
    progressHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '14px',
      marginBottom: '8px',
    },
    progressLabel: {
      color: '#374151',
      fontWeight: 500,
    },
    progressCount: {
      color: '#6b6b6b',
    },
    progressBar: {
      width: '100%',
      backgroundColor: '#e5e0db',
      borderRadius: '9999px',
      height: '8px',
      overflow: 'hidden',
    },
    progressFill: {
      backgroundColor: '#4F6367',
      height: '8px',
      borderRadius: '9999px',
      transition: 'width 0.3s ease',
    },
    checklistContainer: {
      backgroundColor: '#ffffff',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    checklistItem: {
      width: '100%',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '1px solid #e5e0db',
      cursor: 'pointer',
      textAlign: 'left' as const,
      transition: 'background-color 0.2s',
    },
    checklistItemLast: {
      borderBottom: 'none',
    },
    checkIcon: {
      width: '20px',
      height: '20px',
      color: '#16a34a',
      flexShrink: 0,
    },
    uncheckIcon: {
      width: '20px',
      height: '20px',
      color: '#9ca3af',
      flexShrink: 0,
    },
    itemContent: {
      flex: 1,
    },
    itemLabelContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    itemLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#2d2d2d',
    },
    itemLabelChecked: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#2d2d2d',
      textDecoration: 'line-through',
    },
    requiredBadge: {
      fontSize: '12px',
      padding: '2px 8px',
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
      borderRadius: '9999px',
      fontWeight: 500,
    },
    warningBox: {
      backgroundColor: '#fffbeb',
      border: '1px solid #fde68a',
      borderRadius: '8px',
      padding: '16px',
    },
    warningContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    },
    warningIcon: {
      width: '20px',
      height: '20px',
      color: '#d97706',
      marginTop: '2px',
      flexShrink: 0,
    },
    warningTitle: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#92400e',
      margin: 0,
    },
    warningText: {
      fontSize: '12px',
      color: '#a16207',
      marginTop: '4px',
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
    },
    continueButton: {
      flex: 1,
      padding: '12px 24px',
      backgroundColor: '#4F6367',
      color: '#ffffff',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'background-color 0.2s, opacity 0.2s',
    },
    continueButtonDisabled: {
      flex: 1,
      padding: '12px 24px',
      backgroundColor: '#4F6367',
      color: '#ffffff',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'not-allowed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      opacity: 0.5,
    },
    skipButton: {
      padding: '12px 24px',
      backgroundColor: '#e5e0db',
      color: '#374151',
      borderRadius: '8px',
      border: 'none',
      fontWeight: 500,
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    infoList: {
      fontSize: '12px',
      color: '#6b6b6b',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    infoItem: {
      marginBottom: '4px',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.headerBox}>
        <div style={styles.headerContent}>
          <ClipboardList style={styles.headerIcon} />
          <div>
            <h3 style={styles.headerTitle}>Pre-Session Checklist</h3>
            <p style={styles.headerSubtitle}>Session with {clientName}</p>
            <p style={styles.headerHint}>
              Complete all required items before starting the session
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={styles.progressContainer}>
        <div style={styles.progressHeader}>
          <span style={styles.progressLabel}>Required Items</span>
          <span style={styles.progressCount}>
            {requiredChecked} of {requiredItems.length}
          </span>
        </div>
        <div style={styles.progressBar}>
          <div
            style={{
              ...styles.progressFill,
              width: `${(requiredChecked / requiredItems.length) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      <div style={styles.checklistContainer}>
        {checklistItems.map((item, index) => {
          const isChecked = checkedItems[item.id] || false;
          const isLast = index === checklistItems.length - 1;

          return (
            <button
              key={item.id}
              onClick={() => handleToggleItem(item.id)}
              style={{
                ...styles.checklistItem,
                ...(isLast ? styles.checklistItemLast : {}),
              }}
              onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
              onMouseOut={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {isChecked ? (
                <CheckCircle style={styles.checkIcon} />
              ) : (
                <Circle style={styles.uncheckIcon} />
              )}

              <div style={styles.itemContent}>
                <div style={styles.itemLabelContainer}>
                  <span style={isChecked ? styles.itemLabelChecked : styles.itemLabel}>
                    {item.label}
                  </span>
                  {item.required && (
                    <span style={styles.requiredBadge}>Required</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Warning if required items not checked */}
      {!allRequiredChecked && (
        <div style={styles.warningBox}>
          <div style={styles.warningContent}>
            <AlertCircle style={styles.warningIcon} />
            <div>
              <p style={styles.warningTitle}>Required Items Not Complete</p>
              <p style={styles.warningText}>
                Please check all required items before proceeding to check-in
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div style={styles.buttonContainer}>
        <button
          onClick={handleContinue}
          disabled={!allRequiredChecked}
          style={allRequiredChecked ? styles.continueButton : styles.continueButtonDisabled}
          onMouseOver={(e) => {
            if (allRequiredChecked) e.currentTarget.style.backgroundColor = '#3d4d50';
          }}
          onMouseOut={(e) => {
            if (allRequiredChecked) e.currentTarget.style.backgroundColor = '#4F6367';
          }}
        >
          <Package style={{ width: '20px', height: '20px' }} />
          Continue to Photo Verification
        </button>

        {onSkip && (
          <button
            onClick={onSkip}
            style={styles.skipButton}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#d1d5db')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#e5e0db')}
          >
            Skip
          </button>
        )}
      </div>

      {/* Info */}
      <ul style={styles.infoList}>
        <li style={styles.infoItem}>• This checklist ensures you're prepared for an effective session</li>
        <li style={styles.infoItem}>• Required items must be completed for HIPAA compliance</li>
        <li style={styles.infoItem}>• Your responses are logged for quality assurance</li>
      </ul>
    </div>
  );
};
